import { Link } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import './Pricing.css';

const LAST_UPDATED = 'June 2026';

const PLANS = [
  {
    name: 'Basic',
    tag: 'Starter Plan',
    price: 600,
    color: '#0057FF',
    popular: false,
    features: [
      { label: 'Riders', value: 'Up to 25' },
      { label: 'Companies', value: 'Up to 2' },
      { label: 'Stores', value: 'Up to 5' },
      { label: 'Heat Map', value: '✓' },
      { label: 'Wallet', value: '✓' },
      { label: 'Accounting', value: '✓' },
      { label: 'Planned Shifts', value: '✓' },
      { label: 'Locations', value: '✓' },
      { label: 'Dispatch', value: 'Manual / Auto' },
    ],
  },
  {
    name: 'Pro',
    tag: 'Most Popular',
    price: 900,
    color: '#00D4FF',
    popular: true,
    features: [
      { label: 'Riders', value: 'Up to 50' },
      { label: 'Companies', value: 'Up to 5' },
      { label: 'Stores', value: 'Up to 20' },
      { label: 'Heat Map', value: '✓' },
      { label: 'Wallet', value: '✓' },
      { label: 'Accounting', value: '✓' },
      { label: 'Planned Shifts', value: '✓' },
      { label: 'Locations', value: '✓' },
      { label: 'Live Tracking', value: '✓' },
      { label: 'Agent Custody', value: '✓' },
      { label: 'SMS', value: 'Credit-based' },
      { label: 'Dispatch', value: 'Manual / Auto' },
      { label: 'Bundle', value: '✓' },
      { label: 'Mobile App', value: 'Included' },
    ],
  },
  {
    name: 'Premium',
    tag: 'Full Suite',
    price: 1500,
    color: '#7B61FF',
    popular: false,
    features: [
      { label: 'Riders', value: 'Unlimited' },
      { label: 'Companies', value: 'Up to 10' },
      { label: 'Stores', value: 'Unlimited' },
      { label: 'Heat Map', value: '✓' },
      { label: 'Wallet', value: '✓' },
      { label: 'Accounting', value: '✓' },
      { label: 'Planned Shifts', value: '✓' },
      { label: 'Locations', value: '✓' },
      { label: 'Live Tracking', value: '✓' },
      { label: 'Agent Custody', value: '✓' },
      { label: 'SMS', value: '✓' },
      { label: 'Dispatch', value: 'Manual / Auto' },
      { label: 'Bundle', value: '✓' },
      { label: 'Smart Routes', value: '✓' },
      { label: 'Broadcasting', value: '✓' },
      { label: 'Fraud Detection', value: '✓' },
      { label: 'Analytics', value: '✓' },
      { label: 'Items', value: '✓' },
      { label: 'Ranking Boards', value: '✓' },
      { label: 'Customers', value: '✓' },
      { label: 'Trips', value: '✓' },
    ],
  },
];

export default function Pricing() {
  const { lang } = useLang();
  const isAr = lang === 'ar';

  return (
    <main className="pricing-page" dir={isAr ? 'rtl' : 'ltr'}>

      {/* HERO */}
      <section className="pricing-hero">
        <div className="container">
          <p className="section-label">Pricing</p>
          <h1>Route Rover <span className="gradient-text">Pricing</span></h1>
          <p>Simple, transparent pricing. Choose the plan that fits your operation.</p>
          <p className="pricing-updated">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      {/* PLANS */}
      <section className="pricing-plans">
        <div className="container pricing-plans__grid">
          {PLANS.map((plan, i) => (
            <div key={i} className={`pricing-card${plan.popular ? ' pricing-card--popular' : ''}`}>
              {plan.popular && <div className="pricing-card__badge">Most Popular</div>}
              <div className="pricing-card__header">
                <span className="pricing-card__tag" style={{ color: plan.color }}>{plan.tag}</span>
                <h2 className="pricing-card__name">{plan.name} Plan</h2>
                <div className="pricing-card__price">
                  <span className="pricing-card__amount" style={{ color: plan.color }}>${plan.price}</span>
                  <span className="pricing-card__period">/month</span>
                </div>
              </div>
              <div className="pricing-card__features">
                {plan.features.map((f, j) => (
                  <div className="pricing-card__row" key={j}>
                    <span className="pricing-card__feat-label">{f.label}</span>
                    <span className="pricing-card__feat-value" style={{ color: f.value === '✓' ? plan.color : undefined }}>{f.value}</span>
                  </div>
                ))}
              </div>
              <Link to={`/contact?plan=${plan.name}`} className="pricing-card__cta" style={{ background: plan.popular ? plan.color : 'transparent', borderColor: plan.color, color: plan.popular ? '#fff' : plan.color }}>
                Get Started
              </Link>
            </div>
          ))}

          {/* ENTERPRISE */}
          <div className="pricing-card pricing-card--enterprise">
            <div className="pricing-card__header">
              <span className="pricing-card__tag" style={{ color: '#FF6B35' }}>Custom</span>
              <h2 className="pricing-card__name">Enterprise Plan</h2>
              <div className="pricing-card__price">
                <span className="pricing-card__amount" style={{ color: '#FF6B35', fontSize: '32px' }}>Contact Us</span>
              </div>
              <p className="pricing-card__enterprise-desc">Tailored solutions for large fleets, custom integrations, dedicated support, and SLA guarantees.</p>
            </div>
            <div className="pricing-card__features">
              {['Everything in Premium', 'Unlimited everything', 'Custom integrations', 'Dedicated account manager', 'SLA guarantee', 'Priority support 24/7', 'Custom onboarding'].map((f, j) => (
                <div className="pricing-card__row" key={j}>
                  <span className="pricing-card__feat-value" style={{ color: '#FF6B35' }}>✓</span>
                  <span className="pricing-card__feat-label">{f}</span>
                </div>
              ))}
            </div>
            <Link to="/contact" className="pricing-card__cta" style={{ background: '#FF6B35', borderColor: '#FF6B35', color: '#fff' }}>
              Get a Quote
            </Link>
          </div>

        </div>
      </section>

    </main>
  );
}
